import {HttpErrorResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {ApiResponse, CompoApiService, Environment, HTTP_STATUS, InitData, StoreService} from 'compob2b-libs';
import {BehaviorSubject, Observable, throwError,} from 'rxjs';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {CompoToken} from '../interfaces/CompoToken';
import {ImpersonateData} from '../interfaces/ImpersonateData';
import {JwtToken} from '../interfaces/JwtToken';
import {SignData} from '../interfaces/SignData';
import {TokensData} from '../interfaces/TokensData';
import {AuthStateService} from './auth-state.service';

export const TOKEN_NAME = 'compob2bauth';
export const REFRESH_TOKEN_NAME = 'compob2refresh';
export const ROLE_ANONYMOUS = 'ROLE_ANONYMOUS';
export const ROLE_ACCOUNT = 'ROLE_ACCOUNT';
export const ROLE_MANAGER = 'ROLE_MANAGER';
export const ROLE_ADMIN = 'ROLE_ADMIN';
export const ROLE_SUB_ACCOUNT = 'ROLE_SUB_ACCOUNT';
export const ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loading = false;

  constructor(
    private readonly apiService: CompoApiService,
    private readonly stateService: AuthStateService,
    private readonly router: Router,
    private readonly storeService: StoreService,
    private readonly jwtHelper: JwtHelperService,
    @Inject('environment') private readonly environment: Environment
  ) {
  }

  private readonly authenticated = new BehaviorSubject<boolean>(false);
  private isRefreshing = false;

  static tokenGetter() {
    return localStorage.getItem(TOKEN_NAME);
  }

  setRefreshing(refreshing): void {
    this.isRefreshing = refreshing;
  }

  getRefreshing(): boolean {
    return this.isRefreshing;
  }

  refreshToken(): Observable<ApiResponse<TokensData> | Error> {
    const refreshToken: string = this.getRefreshToken();

    /**
     * If we have expired refresh token and we want user to stay at portal
     * without logging out - we need renew anonymous tokens data
     */
    if (!refreshToken || this.jwtHelper.isTokenExpired(refreshToken)) {
      if (this.environment.isOpenCabinet) {
        return this.getAnonymousTokens();
      }

      console.log('POINT 4');
      this.logout();
    }

    const refresh: CompoToken = {
      token: refreshToken,
      info: 'refresh'
    };

    return this.apiService
      .post<CompoToken, TokensData>('/refresh/', refresh)
      .pipe(
        tap(_ => this.setRefreshing(true)),
        tap(response => this.successHandler(response, this.isRemember()))
      );
  }

  initCustomer(): Observable<ApiResponse<InitData>> {
    return this.storeService.initData();
  }

  /**
   * Manager can impersonate any user with
   * managers token and user's id
   *
   * @param data
   * @private
   */
  public impersonate(data: ImpersonateData) {
    return this.apiService.post<ImpersonateData, TokensData>('/impersonate', data).pipe(
      tap((response: ApiResponse<TokensData>) =>
        this.successHandler(response, false)
      ),
      switchMap(_ => this.storeService.initData()),
      catchError(error => this.errorHandler(error))
    );
  }

  /**
   * Calls from sign-in.component
   * Signing in and doing other operations
   * after successful authorization: save tokens, initialize storage data, redirect to cabinet
   * @param form sing in form controls
   */
  public signIn(form: SignData): Observable<ApiResponse<InitData> | Error> {
    this.loading = true;
    this.clearStorage();

    return this.apiService.post<SignData, TokensData>('/sign-in/', form).pipe(
      take(1),
      tap((response: ApiResponse<TokensData>) =>
        this.successHandler(response, form.remember)
      ),
      switchMap(_ => this.storeService.initData()),
      catchError(error => this.errorHandler(error))
    );
  }

  /**
   *  Get special anonymous tokens for unauthorized users
   */
  public checkAndSetAnonymousToken(): Observable<ApiResponse<InitData> | Error> {

    const refreshToken = this.getRefreshToken();

    if (this.getToken() === null || this.jwtHelper.isTokenExpired(refreshToken)) {
      return this.authorizeAsAnonymousAndInit();
    }

    return null;
  }

  private authorizeAsAnonymousAndInit(): Observable<ApiResponse<InitData> | Error> {
    return this.getAnonymousTokens().pipe(
      switchMap(_ => this.storeService.initData()),
      catchError(error => this.errorHandler(error))
    );
  }

  private getAnonymousTokens(): Observable<ApiResponse<TokensData> | Error> {
    return this.apiService.get<TokensData>('/auth-anonymous').pipe(
      tap((response: ApiResponse<TokensData>) =>
        this.successHandler(response, true)
      ),
      catchError(error => this.errorHandler(error)),
    );
  }

  private setToken(token: string, remember: boolean): void {
    localStorage.setItem(TOKEN_NAME, token);
    localStorage.setItem('remember', 'true');
  }

  /**
   * Handles success result of request
   * @param remember boolean - remember me checkbox value
   * @param response ApiResponse
   */
  public successHandler(response: ApiResponse<TokensData>, remember: boolean): void {
    this.setAuthenticated(true);
    this.setRefreshing(false);
    this.loading = false;
    this.storeTokens(
      response.data.access.token,
      response.data.refresh.token,
      remember
    );
    this.stateService.success(HTTP_STATUS.SUCCESS);
  }

  private storeTokens(
    accessToken: string,
    refreshToken: string,
    remember: boolean
  ) {
    this.setToken(accessToken, remember);
    this.setRefreshToken(refreshToken);
  }

  getToken(): string {
    return this.getItemFromStorage(TOKEN_NAME);
  }

  getRoles(): string[] {
    const token = this.getDecodedToken();

    return token?.roles || [];
  }

  private getDecodedToken(): JwtToken {
    return this.jwtHelper.decodeToken(this.getToken());
  }

  public isAnonymous(): boolean {
    return this.isAuthenticated() && this.getRoles().includes(ROLE_ANONYMOUS);
  }

  public isAccount(): boolean {
    return this.isAuthenticated() && this.getRoles().includes(ROLE_ACCOUNT);
  }

  public isSubAccount(): boolean {
    return this.isAuthenticated() && this.getRoles().includes(ROLE_SUB_ACCOUNT);
  }

  public isCustomer(): boolean {
    return this.isAuthenticated() && (this.getRoles().includes(ROLE_ACCOUNT) || this.getRoles().includes(ROLE_SUB_ACCOUNT));
  }

  public isManager(): boolean {
    return this.isAuthenticated() && this.getRoles().includes(ROLE_MANAGER);
  }

  public isSuperAdmin(): boolean {
    return this.isAuthenticated() && this.getRoles().includes(ROLE_SUPER_ADMIN);
  }

  public isAuthenticated(): Observable<boolean> {
    const token: string = this.getToken();

    if (!token) {
      this.abort(!this.environment.isOpenCabinet);
    }

    /**
     * If token is expired - try to refresh or logout
     * Auth guard catches this observable and launch subscription when
     * return result of CanActivate method
     */
    try {
      if (this.jwtHelper.isTokenExpired(token)) {
        return this.refreshToken().pipe(
          switchMap(_ => this.getAuthenticated())
        );
      }
      else {
        this.setAuthenticated(true);
      }
    } catch (e) {
      console.error(e);
      this.abort(true);
    }

    return this.getAuthenticated();
  }

  private abort(logout: boolean): void {
    this.setAuthenticated(false);
    if (logout) {
      this.logout();
    }
  }

  private getAuthenticated(): Observable<boolean> {
    return this.authenticated.asObservable();
  }

  private setAuthenticated(authenticated: boolean): void {
    this.authenticated.next(authenticated);
  }

  logout(): void {
    if (!this.environment.isOpenCabinet) {
      this.setAuthenticated(false);
      this.setRefreshing(false);
      this.clearStorage();
      this.redirectToSignIn();
    }
    else {
      this.clearStorage();
      window.location.reload();
    }
  }

  clearStorage(): void {
    localStorage.removeItem(TOKEN_NAME);
    localStorage.removeItem(REFRESH_TOKEN_NAME);
    localStorage.removeItem('remember');
    localStorage.removeItem('user');
    localStorage.removeItem('settings');
    sessionStorage.clear();
  }

  redirectToSignIn(): void {
    this.router.navigate(['sign-in']);
  }

  private errorHandler(errorResponse: HttpErrorResponse): Observable<Error> {
    this.loading = false;
    this.stateService.fail(errorResponse.status);

    if (errorResponse.status !== HTTP_STATUS.FORBIDDEN && errorResponse.status !== HTTP_STATUS.UNAUTHORIZED) {
      this.clearStorage();
      this.stateService.setServerError();
    }

    if (errorResponse.headers.get('compo-info') === 'USER_LOCKED') {
      this.stateService.setLockedError();
    }

    // throw errorResponse;
    return throwError(errorResponse);
  }

  private isRemember(): boolean {
    return true;
  }

  public getRefreshToken(): string {
    return this.getItemFromStorage(REFRESH_TOKEN_NAME);
  }

  private setRefreshToken(refreshToken: string) {
    this.isRemember()
      ? localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken)
      : sessionStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
  }

  private getItemFromStorage(name: string): string {
    return this.isRemember()
      ? localStorage.getItem(name)
      : sessionStorage.getItem(name);
  }
}
